import type { SubscriptionData } from "@repo/dashboard-core";

import { isOngoing, type TopUp } from "@/lib/types/topup";

/** `/members/subscriptions` sends the whole Subscription row; these two are
 * the ones the review window needs, with `startDate`. */
type TrialSubscription = SubscriptionData & {
  trialEndDate?: string | null;
  rebillCount?: number;
};

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * GD-125 — the top-up the Trustpilot review card asks about, if any.
 *
 * The newest delivered top-up, card PIN or not, so a later still-ongoing or
 * failed top-up doesn't take the card away (TD-150 picks the newest reviewable
 * purchase the same way). `/members/topups` no longer sends a status
 * (GAPI-854); Delivered is what the list shows, `isOngoing` false. `topUps` is
 * newest first.
 *
 * Only while some active subscription is in its first 3 days, before its first
 * rebill (`rebillCount` goes to 1 then): inside its trial, or from the start of
 * a plan with no trial, which is charged at sign-up. A cancelled one doesn't
 * count. This only spares the API call: the API checks the top-up's own
 * subscription (GAPI-997), which this list can't tell apart.
 */
export function reviewableTopUp(
  topUps: TopUp[],
  subscriptions: SubscriptionData[] | undefined,
  now = Date.now(),
): TopUp | undefined {
  const latest = topUps.find((topUp) => !isOngoing(topUp));
  if (!latest) return undefined;

  const inWindow = subscriptions?.some((subscription) => {
    const { status, startDate, trialEndDate, rebillCount } =
      subscription as TrialSubscription;
    if (status !== "active" || rebillCount !== 0) return false;

    // A plan with no trial (tu_subscription_1m_9) has no trial dates at all.
    return trialEndDate
      ? new Date(trialEndDate).getTime() > now
      : new Date(startDate).getTime() > now - THREE_DAYS_MS;
  });

  return inWindow ? latest : undefined;
}
